/* @flow */

import * as React from 'react';
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import PropTypes from 'prop-types';
import RequireForAccess from 'react-access';
import { RouterStore } from 'mobx-react-router';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { allRoles } from 'permissions/general';
import type { RoleList } from 'permissions/defs';
import NavigationStore from 'stores/NavigationStore';
import UserStore from 'stores/UserStore';

import styles from './PageTitle.module.scss';

type TypeMultipleLinks = {|
  id: number,
  label: string,
  link: string,
|};

type InjectedProps = {
  navigationStore: NavigationStore,
  routing: RouterStore,
  userStore: UserStore,
} & TranslatorProps;

export type AdditionalButtonSpec = {
  label: string,
  icon?: string,
  onClick: () => void,
  permissions: RoleList,
};

export type PageTitleProps = {
  title: string,
  addButton?: boolean,
  isCentred?: boolean,
  label?: string,
  path?: string,
  total?: string | number,
  addMultipleLinks?: TypeMultipleLinks[],
  cancel?: boolean,
  permissions: RoleList,
  additionalButtons?: AdditionalButtonSpec[],
  titleSlot?: React.Node,
  onClick?: () => void,
};

/**
 * @deprecated You should use the new Page Title if implementing a new page or section
 */
@translate()
@inject('routing', 'navigationStore', 'userStore')
@observer
export default class PageTitle extends ComponentWithInjectedProps<
  PageTitleProps,
  {},
  InjectedProps
> {
  static defaultProps = {
    addButton: false,
    label: 'general:actions.add_new',
    permissions: allRoles,
    isCentred: false,
  };

  @observable anchorEl: any;

  handleClick = (ev: SyntheticInputEvent<*>) => {
    this.anchorEl = ev.currentTarget;
  };

  handleClose = () => {
    this.anchorEl = null;
  };

  formatTitle(title: string): React.Element<*> | string {
    const pathTable: string = this.props.navigationStore.accountPath;

    const pStyle: * = {
      float: 'none',
    };

    if (title.indexOf('|') !== -1) {
      const splitText = title.split('|');
      return (
        <React.Fragment>
          {/* {splitText[0].trim()} <span>|</span> {splitText[1].trim()} */}
          {pathTable[0].path ? (
            <Link style={pStyle} to={pathTable[0].path}>
              {splitText[0].trim()}
            </Link>
          ) : (
            splitText[0].trim()
          )}
          {splitText[1] &&
            (pathTable[1] && this.props.routing.location.pathname !== pathTable[1].path ? (
              <React.Fragment>
                <span>|</span>
                <Link
                  style={pStyle}
                  to={{ pathname: pathTable[1].path, state: { tab: pathTable[1].tab } }}
                >
                  {splitText[1].trim()}
                </Link>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <span>|</span>
                {splitText[1].trim()}
              </React.Fragment>
            ))}
          {splitText[2] &&
            (pathTable[2] && this.props.routing.location.pathname !== pathTable[2].path ? (
              <React.Fragment>
                <span>|</span>
                <Link
                  style={pStyle}
                  to={{ pathname: pathTable[2].path, state: { tab: pathTable[2].tab } }}
                >
                  {splitText[2].trim()}
                </Link>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <span>|</span>
                {splitText[2].trim()}
              </React.Fragment>
            ))}
          {splitText[3] &&
            (pathTable[3] && this.props.routing.location.pathname !== pathTable[3].path ? (
              <React.Fragment>
                <span>|</span>
                <Link style={pStyle} to={pathTable[3].path}>
                  {splitText[3].trim()}
                </Link>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <span>|</span>
                {splitText[3].trim()}
              </React.Fragment>
            ))}
        </React.Fragment>
      );
    }

    return title;
  }

  renderAddButton() {
    const {
      cancel,
      label,
      path,
      t,
      userStore: { isReadOnly },
      onClick,
    } = this.props;

    let onClickEvent: Function | null = null;

    if (this.props.addMultipleLinks) {
      onClickEvent = this.handleClick;
    }

    if (this.props.cancel) {
      onClickEvent = onClick || null;
    }

    const button = (
      <Button
        variant={cancel ? 'contained' : 'outlined'}
        color={cancel ? 'secondary' : 'default'}
        onClick={onClickEvent}
        disabled={isReadOnly}
      >
        {cancel ? null : (
          <svg className="button-icon">
            <use xlinkHref="#icon-add" />
          </svg>
        )}
        {t(label)}
      </Button>
    );

    if (path) {
      return <Link to={isReadOnly ? '#' : path}>{button}</Link>;
    }

    return button;
  }

  renderAdditionalButton = (additionalButton: AdditionalButtonSpec) => (
    <RequireForAccess
      key={`additional-button-${additionalButton.label}`}
      permissions={additionalButton.permissions}
    >
      <div className={styles['add-button']}>
        <Button variant="outlined" color="default" onClick={additionalButton.onClick}>
          {additionalButton.icon && (
            <svg className="button-icon">
              <use xlinkHref={additionalButton.icon} />
            </svg>
          )}
          {this.props.t(additionalButton.label)}
        </Button>
      </div>
    </RequireForAccess>
  );

  render() {
    const {
      title,
      addButton,
      addMultipleLinks,
      additionalButtons,
      isCentred,
      titleSlot,
      t,
    } = this.props;
    const total = this.props.total ? `(${this.props.total})` : '';
    const centredClass = isCentred ? styles['is-centred'] : '';

    return (
      <div className={`${styles['page-title']} ${centredClass}`}>
        <div className={styles.titleWrapper}>
          <h1>
            {title && this.formatTitle(title)} {total}
          </h1>
          {titleSlot}
        </div>
        <React.Fragment>
          {additionalButtons && additionalButtons.map(this.renderAdditionalButton)}
          {addButton && (
            <RequireForAccess permissions={this.props.permissions}>
              <div className={styles['add-button']}>{this.renderAddButton()}</div>
            </RequireForAccess>
          )}
          {addMultipleLinks && (
            <RequireForAccess permissions={this.props.permissions}>
              <Menu
                className={styles['links-menu']}
                anchorEl={this.anchorEl}
                open={Boolean(this.anchorEl)}
                onClose={this.handleClose}
                getContentAnchorEl={null}
                elevation={0}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                BackdropProps={{
                  invisible: false,
                }}
                PaperProps={{
                  component: 'nav',
                }}
              >
                {addMultipleLinks.map((item: Object) => (
                  <MenuItem key={item.id}>
                    <Link to={item.link}>
                      <Button variant="contained" color="primary" onClick={this.handleClose}>
                        {t(item.label)}
                      </Button>
                    </Link>
                  </MenuItem>
                ))}
              </Menu>
            </RequireForAccess>
          )}
        </React.Fragment>
      </div>
    );
  }
}

PageTitle.propTypes = {
  title: PropTypes.string.isRequired,
  addButton: PropTypes.bool,
  isCentred: PropTypes.bool,
  label: PropTypes.string,
  path: PropTypes.string,
  total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  addMultipleLinks: PropTypes.array,
  cancel: PropTypes.bool,
  permissions: PropTypes.array,
};
